import { useRolodexEntries } from 'features/rolodex'
import { Loader2 } from 'lucide-react'
import React from 'react'
import styled, { keyframes } from 'styled-components'
import { type GeckoKestrelModel, RolodexEntryType } from 'types'

import { useHasKestrel3DTiles } from './hooks/useHasKestrel3DTiles'
import { ReadOnly3DTilesProvider } from './ReadOnly3DTilesContext'
import { ReadOnly3DTilesViewer } from './ReadOnly3DTilesViewer'

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

const ViewerContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  overflow: hidden;
  background-color: #1a1a2e;
  position: relative;
`

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: rgba(255, 255, 255, 0.8);
  font-size: 1rem;
  gap: 1rem;
  background-color: #1a1a2e;

  svg {
    animation: ${spin} 1s linear infinite;
  }
`

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
  gap: 0.5rem;
  background-color: #1a1a2e;
  text-align: center;
  padding: 2rem;
`

interface ReadOnlyModelViewerProps {
  kestrelModelId: string
}

/**
 * Self-contained 3D model viewer component.
 * Drop this into any container div - it will fill 100% width and height.
 * Only requires the kestrel model ID - handles data fetching internally.
 */
export const ReadOnlyModelViewer: React.FC<ReadOnlyModelViewerProps> = ({
  kestrelModelId,
}) => {
  const { kestrelModels, isLoading } = useRolodexEntries(
    kestrelModelId
      ? {
          ids: [kestrelModelId],
          type: RolodexEntryType.KESTREL_MODEL,
          limit: 1,
        }
      : undefined
  )

  const model: GeckoKestrelModel | undefined = kestrelModels.at(0)
  const { has3DTiles, isLoading: tilesLoading } =
    useHasKestrel3DTiles(kestrelModelId)

  if (isLoading || tilesLoading) {
    return (
      <LoadingContainer>
        <Loader2 size={32} />
        <span>Loading model...</span>
      </LoadingContainer>
    )
  }

  if (!model) {
    return (
      <ErrorContainer>
        <div>Model not found</div>
        <div style={{ opacity: 0.5, fontSize: '0.8rem' }}>{kestrelModelId}</div>
      </ErrorContainer>
    )
  }

  if (!has3DTiles) {
    return <></>
  }

  return (
    <ReadOnly3DTilesProvider>
      <ViewerContainer>
        <ReadOnly3DTilesViewer
          kestrelModelId={kestrelModelId}
          kestrelModel={model}
        />
      </ViewerContainer>
    </ReadOnly3DTilesProvider>
  )
}
